// Shared service catalog used on Home cards and individual detail pages.
import { Plane, Ship, Package, FileText, Layers, Navigation, AlertTriangle, Thermometer } from "lucide-react";

export const SERVICES = [
  {
    slug: "air-freight",
    icon: Plane,
    title: "Air Freight",
    short:
      "We offer tailored air freight solutions for shipments of all sizes with a focus on speed, reliability and cost efficiency through our global partner network.",
    img: "/assets/air freight.png",
    hero: "When speed is everything, we take it skyward.",
    paragraphs: [
      "We offer customer-specific air freight solutions tailored to shipments of all sizes with a strong focus on speed, flexibility and cost efficiency. Through our global network of partners and direct relationships with major airlines, we ensure priority handling and consistent communication throughout the shipment lifecycle.",
      "Our capabilities include time-critical shipments with 24/7 support, next flight out services and guaranteed space allocation with leading carriers. We also provide door-to-door delivery worldwide along with packaging, palletizing and warehousing support. Our team is experienced in handling special cargo including hazardous materials and arranging full or part charters.",
    ],
    features: [
      "Time-critical shipments with 24/7 support",
      "Next flight out and guaranteed space allocation",
      "Door-to-door delivery worldwide",
      "Hazardous cargo handling and charter arrangements",
      "Packaging, palletizing and warehousing support",
    ],
  },
  {
    slug: "ocean-freight",
    icon: Ship,
    title: "Ocean Freight",
    short:
      "Cost-effective and reliable FCL and LCL solutions with complete process management from carrier selection and rate optimization to final delivery.",
    img: "/assets/ocean freight.png",
    hero: "Reliable ocean freight across every major trade lane.",
    paragraphs: [
      "Our ocean freight services are designed to provide cost-effective and reliable solutions for both Full Container Load (FCL) and Less than Container Load (LCL) shipments. We manage the complete process from planning and carrier selection to final delivery.",
      "With expertise in rate analysis, negotiation and carrier booking, we ensure optimal routing and cost efficiency. Our services include door-to-door solutions, inland transportation, containerization, warehousing and storage. We are also equipped to handle breakbulk cargo, special equipment shipments and complex project movements including full and part charter arrangements.",
    ],
    features: [
      "FCL and LCL shipments on weekly sailings",
      "Carrier selection and rate optimization",
      "Door-to-door and inland transport services",
      "Warehousing and storage",
      "Breakbulk and special equipment handling",
      "Full and part charter arrangements",
    ],
  },
  {
    slug: "import-consolidation-customs",
    icon: Package,
    title: "Import Consolidation & Customs Brokerage",
    short:
      "Strong inbound consolidation services from all major global regions with integrated customs brokerage ensuring compliant and timely clearance.",
    img: "/assets/consolidation.avif",
    hero: "Seamless consolidation and clearance from origin to door.",
    paragraphs: [
      "We offer strong inbound consolidation services from all major global regions enabling efficient cargo movement and cost optimization. Our integrated approach ensures seamless coordination from origin to final delivery.",
      "Our customs brokerage services ensure accurate and compliant clearance processes supported by in-depth regulatory knowledge. We also provide distribution and delivery services, just-in-time inventory coordination, duty drawback assistance and continuous shipment tracking. Our team is experienced in handling project cargo and maintaining detailed classification databases for smooth processing.",
    ],
    features: [
      "Regular inbound consolidation from all major regions",
      "Customs clearance and regulatory compliance",
      "Duty optimization and drawback assistance",
      "Distribution and last-mile delivery",
      "Just-in-time inventory coordination",
      "Shipment tracking and project cargo handling",
    ],
  },
  {
    slug: "documentation-compliance",
    icon: FileText,
    title: "Documentation & Compliance",
    short:
      "End-to-end documentation management with full compliance to international trade regulations including consular certifications and licensing support.",
    img: "/assets/documentation and complince.png",
    hero: "Accurate documentation. Smooth compliance. Zero delays.",
    paragraphs: [
      "We manage all aspects of logistics documentation with precision and efficiency ensuring full compliance with international trade and regulatory requirements.",
      "Our services include preparation of import and export documentation, consular certifications and assistance with obtaining licenses from Indian authorities. We also provide notary, chamber of commerce and legalization services. With a strong focus on accuracy and timelines, we ensure smooth processing and keep clients updated at every stage.",
    ],
    features: [
      "Import and export documentation preparation",
      "Consular certifications",
      "Licensing support from Indian authorities",
      "Notary, chamber of commerce and legalization services",
      "Real-time shipment updates",
      "Full compliance with international trade regulations",
    ],
  },
  {
    slug: "project-cargo",
    icon: Layers,
    title: "Project Cargo",
    short:
      "Specialized handling of complex, oversized and high-value project shipments with detailed planning, route analysis and end-to-end execution.",
    img: "/assets/project cargo.png",
    hero: "Complex cargo. Meticulous planning. Delivered safely.",
    paragraphs: [
      "We specialize in handling complex, oversized and high-value project cargo that requires detailed planning and execution. From route analysis and cargo handling to coordination and delivery, we ensure safe, efficient and timely movement across all stages.",
      "Our project cargo team has the expertise to manage heavy-lift shipments, out-of-gauge freight and multi-modal movements. We work closely with clients to develop customized solutions that meet specific operational and timeline requirements.",
    ],
    features: [
      "Oversized and out-of-gauge cargo handling",
      "Route survey and analysis",
      "Heavy-lift and multi-modal coordination",
      "Detailed planning and execution",
      "High-value cargo protection",
      "End-to-end visibility and reporting",
    ],
  },
  {
    slug: "door-to-door",
    icon: Navigation,
    title: "Door-to-Door Solutions",
    short:
      "Complete end-to-end logistics from origin to final destination covering pickup, freight movement, customs clearance and last-mile delivery.",
    img: "/assets/door-to-door.avif",
    hero: "One call. Every mile. Delivered.",
    paragraphs: [
      "Our door-to-door services provide complete end-to-end logistics support covering pickup, freight movement, customs clearance and final delivery. This integrated approach ensures convenience, transparency and reduced transit complexities for our clients.",
      "We coordinate every step of the shipment lifecycle so our clients can focus on their business. Whether it is a single parcel or a large commercial consignment, we handle it with the same level of care and efficiency.",
    ],
    features: [
      "Origin pickup and packaging",
      "Air and ocean freight movement",
      "Customs clearance at destination",
      "Last-mile delivery to final address",
      "End-to-end shipment tracking",
      "Single point of contact throughout",
    ],
  },
  {
    slug: "dg-hazardous-valuable",
    icon: AlertTriangle,
    title: "DG, Hazardous & Valuable Cargo",
    short:
      "Fully compliant handling of dangerous goods, hazardous materials and high-value shipments with proper packaging, labeling and secure transportation.",
    img: "/assets/dangerous and hazardous cargo.PNG",
    hero: "High-risk cargo handled with precision and compliance.",
    paragraphs: [
      "We are equipped to handle dangerous goods (DG), hazardous materials and high-value cargo in full compliance with international safety standards. Our team ensures proper packaging, labeling, documentation and secure transportation to minimize risk and ensure safe delivery.",
      "Our certified professionals are trained in IATA and IMDG regulations and work with airlines and shipping lines experienced in DG cargo. We manage all required permits, declarations and handling instructions to ensure full regulatory compliance throughout the journey.",
    ],
    features: [
      "IATA and IMDG certified DG handling",
      "Proper packaging and labeling",
      "Complete regulatory documentation",
      "Secure transportation arrangements",
      "High-value cargo protection",
      "End-to-end compliance monitoring",
    ],
  },
  {
    slug: "cold-chain",
    icon: Thermometer,
    title: "Cold Chain Logistics",
    short:
      "Specialized temperature-controlled logistics solutions for sensitive cargo such as pharmaceuticals and chemicals maintaining product integrity throughout.",
    img: "/assets/cold-chain.avif",
    hero: "Temperature precision. Product integrity. Every time.",
    paragraphs: [
      "We provide specialized temperature-controlled logistics solutions for sensitive cargo such as pharmaceuticals and chemicals. Our cold chain services maintain strict temperature integrity across the entire supply chain ensuring product quality and compliance.",
      "Our team coordinates with qualified carriers, temperature-controlled warehouses and regulatory authorities to manage the complete cold chain process. We provide real-time temperature monitoring and documentation throughout the shipment lifecycle.",
    ],
    features: [
      "Temperature-controlled air and sea freight",
      "Pharmaceutical and chemical cargo expertise",
      "Real-time temperature monitoring",
      "Qualified cold chain carriers and facilities",
      "Complete regulatory documentation",
      "Product quality and compliance assurance",
    ],
  },
];

export const getService = (slug) => SERVICES.find((s) => s.slug === slug);
